import { createWebTracker } from '@theneatcompany/event-client/web'
import { userAgent } from '@theneatcompany/nui'

import { ENV } from '@/config'

const ANON_ID_KEY = 'neat.anonymousId'
const CLIENT_HINTS_HIGH_ENTROPY_VALUES = ['platformVersion', 'fullVersionList', 'model']
const CLIENT_HINTS_FIRST_TRACK_WAIT_MS = 100

let tracker
let clientHintsContext
let clientHintsPromise
let hasClientHintsResolved = false
let firstTrackClientHintsWaitPromise

const isClient = () => typeof window !== 'undefined'
const wait = delayMs => new Promise(resolve => setTimeout(resolve, delayMs))

function getOrCreateAnonymousId() {
  if (!isClient()) return undefined

  try {
    const existing = localStorage.getItem(ANON_ID_KEY)
    if (existing) return existing

    const next = crypto.randomUUID()
    localStorage.setItem(ANON_ID_KEY, next)
    return next
  } catch {
    return crypto.randomUUID()
  }
}

const { browser, device, os } = userAgent

const getEnvironment = () => {
  switch (ENV) {
    case 'staging':
    case 'production':
    case 'development':
      return ENV
    default:
      return 'qa'
  }
}

const environment = getEnvironment()

const getApiEnvironment = () => {
  switch (environment) {
    case 'development':
      return process.env.NEXT_PUBLIC_API === 'staging' ? 'staging' : 'qa'
    default:
      return environment
  }
}

const apiEnvironment = getApiEnvironment()

const getClientKey = () => {
  switch (apiEnvironment) {
    case 'production':
      return 'aEogFAXznLdC9S53Nc9K4VusqBS0GTQaC2qjdqy3'
    case 'qa':
      return 'pGaJiNABmc3HGrtXtLWEu3sZZScv6tRp7xvsbYXT'
    default:
      return '9ReZeowC1J3sV6NsemYEB1OFSsRBdpjma6BL0a5g'
  }
}

const getDebugEnabled = () => {
  if (!isClient()) return false

  try {
    const lsValue = localStorage.getItem('EVENT_CLIENT_DEBUG')
    if (lsValue === 'false') return false
    return ENV === 'development' || lsValue === 'true'
  } catch {
    return ENV === 'development'
  }
}

const ENABLED = ENV !== 'test' && ENV !== 'development'

const getBaseContext = () => ({
  appName: 'Paper Monster',
  appVersion: process.env.NEXT_PUBLIC_BUILD_TAG,
  browser: {
    name: browser.name,
    version: browser.version,
  },
  device: {
    model: device.model,
    manufacturer: device.vendor,
    osName: os.name,
    osVersion: os.version,
  },
})

const isGreaseBrand = (brand = '') => {
  const normalizedBrand = brand.toLowerCase()
  return (
    normalizedBrand.includes('not') ||
    normalizedBrand.includes('brand') ||
    /[();?/]/.test(brand)
  )
}

const normalizeBrand = (value = '') => value.toLowerCase().replace(/[^a-z0-9]/g, '')

const getBrowserBrandAliases = (browserName = '') => {
  const normalizedBrowserName = normalizeBrand(browserName)

  if (normalizedBrowserName.includes('edge')) return ['microsoftedge', 'edge']
  if (normalizedBrowserName.includes('opera')) return ['opera', 'opr']
  if (normalizedBrowserName.includes('chromium')) return ['chromium']
  if (normalizedBrowserName.includes('chrome')) return ['googlechrome', 'chrome', 'chromium']

  return normalizedBrowserName ? [normalizedBrowserName] : []
}

const findBrowserVersion = (fullVersionList = [], browserName) => {
  const realBrands = fullVersionList.filter(({ brand, version }) => brand && version && !isGreaseBrand(brand))
  const aliases = getBrowserBrandAliases(browserName)

  const matchedBrand = aliases
    .map(alias => realBrands.find(({ brand }) => normalizeBrand(brand).includes(alias)))
    .find(Boolean)

  if (matchedBrand) return matchedBrand.version

  const knownChromiumBrand = ['googlechrome', 'microsoftedge', 'opera', 'chromium']
    .map(alias => realBrands.find(({ brand }) => normalizeBrand(brand).includes(alias)))
    .find(Boolean)

  return knownChromiumBrand?.version
}

export async function getClientHintsContext(baseContext, navigatorOverride) {
  const userAgentData = navigatorOverride?.userAgentData ?? (isClient() ? window.navigator?.userAgentData : undefined)

  if (!userAgentData?.getHighEntropyValues) return undefined

  try {
    const highEntropyValues = await userAgentData.getHighEntropyValues(CLIENT_HINTS_HIGH_ENTROPY_VALUES)
    const browserVersion = findBrowserVersion(
      highEntropyValues.fullVersionList,
      baseContext.browser?.name,
    )

    return {
      browser: {
        ...baseContext.browser,
        ...(browserVersion ? { version: browserVersion } : {}),
      },
      device: {
        ...baseContext.device,
        ...(userAgentData.platform ? { osName: userAgentData.platform } : {}),
        ...(highEntropyValues.platformVersion ? { osVersion: highEntropyValues.platformVersion } : {}),
        ...(highEntropyValues.model ? { model: highEntropyValues.model } : {}),
      },
    }
  } catch {
    return undefined
  }
}

const getContextWithClientHints = baseContext => ({
  ...baseContext,
  ...(clientHintsContext?.browser ? { browser: clientHintsContext.browser } : {}),
  ...(clientHintsContext?.device ? { device: clientHintsContext.device } : {}),
})

function startClientHintsLookup(baseContext) {
  if (!isClient()) return Promise.resolve(undefined)
  if (clientHintsPromise) return clientHintsPromise

  clientHintsPromise = getClientHintsContext(baseContext).then(context => {
    clientHintsContext = context
    return context
  }).finally(() => {
    hasClientHintsResolved = true
  })

  return clientHintsPromise
}

export function initializeClientHints() {
  return startClientHintsLookup(getBaseContext())
}

function waitForClientHintsBeforeFirstTrack(baseContext) {
  if (hasClientHintsResolved) return Promise.resolve()

  startClientHintsLookup(baseContext)

  if (!firstTrackClientHintsWaitPromise) {
    firstTrackClientHintsWaitPromise = Promise.race([
      clientHintsPromise,
      wait(CLIENT_HINTS_FIRST_TRACK_WAIT_MS),
    ]).then(() => undefined)
  }

  return firstTrackClientHintsWaitPromise
}

function updateTrackerWithClientHints(activeTracker, baseContext) {
  startClientHintsLookup(baseContext).then(context => {
    if (context) activeTracker.setContext?.(context)
  })
}

function getTracker() {
  if (!isClient()) return null
  if (tracker) return tracker

  const baseContext = getBaseContext()
  tracker = createWebTracker({
    anonymousId: getOrCreateAnonymousId(),
    clientKey: process.env.NEXT_PUBLIC_EVENTS_CLIENT_KEY || getClientKey(),
    environment,
    apiEnvironment,
    getAuthHeaders: () => null,
    context: getContextWithClientHints(baseContext),
    enabled: ENABLED,
    debug: getDebugEnabled(),
  })

  if (!clientHintsContext && !hasClientHintsResolved) updateTrackerWithClientHints(tracker, baseContext)

  return tracker
}

async function trackAfterClientHintsWait(eventName, properties) {
  const baseContext = getBaseContext()
  await waitForClientHintsBeforeFirstTrack(baseContext)
  getTracker()?.track(eventName, properties)
}

export function track(eventName, properties) {
  if (tracker) {
    tracker.track(eventName, properties)
    return
  }

  trackAfterClientHintsWait(eventName, properties)
}

export default getTracker
